import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination } from '@/components/Pagination';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

const PAGE_SIZE = 10;

export default function ManageEmbassies() {
  const { t } = useI18n();
  const [embassies, setEmbassies] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ country: '', city: '', address: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const load = () => api.get('/embassies').then(r => setEmbassies(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ country: '', city: '', address: '', phone: '', email: '' }); setDialog(true); };
  const openEdit = (emb) => { setEditing(emb); setForm({ country: emb.country, city: emb.city, address: emb.address || '', phone: emb.phone || '', email: emb.email || '' }); setDialog(true); };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/embassies/${editing.embassy_id}`, form);
        toast.success(t('admin.save'));
      } else {
        await api.post('/embassies', form);
        toast.success(t('admin.save'));
      }
      setDialog(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.confirm_delete'))) return;
    try {
      await api.delete(`/embassies/${id}`);
      toast.success(t('admin.delete'));
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const paginated = embassies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="manage-embassies-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('admin.embassies')}</h1>
        <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={openNew} data-testid="add-embassy-btn">
          <Plus className="w-4 h-4 mr-2" /> {t('admin.add_embassy')}
        </Button>
      </div>

      <Card className="border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.country')}</TableHead>
                <TableHead>{t('common.city')}</TableHead>
                <TableHead>{t('common.email')}</TableHead>
                <TableHead>{t('common.phone')}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map(emb => (
                <TableRow key={emb.embassy_id}>
                  <TableCell className="font-medium">{emb.country}</TableCell>
                  <TableCell>{emb.city}</TableCell>
                  <TableCell className="text-sm">{emb.email || '-'}</TableCell>
                  <TableCell className="text-sm">{emb.phone || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(emb)} data-testid={`edit-embassy-${emb.embassy_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(emb.embassy_id)} data-testid={`delete-embassy-${emb.embassy_id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 pb-4">
            <Pagination total={embassies.length} pageSize={PAGE_SIZE} currentPage={page} onPageChange={setPage} />
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editing ? t('admin.edit_embassy') : t('admin.new_embassy')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>{t('common.country')} *</Label><Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} data-testid="embassy-country" /></div>
            <div className="space-y-2"><Label>{t('common.city')} *</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} data-testid="embassy-city" /></div>
            <div className="space-y-2"><Label>{t('common.address')}</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} data-testid="embassy-address" /></div>
            <div className="space-y-2"><Label>{t('common.phone')}</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="embassy-phone" /></div>
            <div className="space-y-2"><Label>{t('common.email')}</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="embassy-email" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>{t('common.cancel')}</Button>
            <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleSave} disabled={loading || !form.country || !form.city} data-testid="save-embassy-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editing ? t('common.edit') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
